import { action, computed, makeObservable, observable } from 'mobx';
import { BaseCustomerExperienceData, SharedLocationDisplayType, SharingType } from '@bringg/types';
import { getEnv, getRoot } from 'mobx-easy';
import {
	BaseSharedLocationConfig,
	CancelEventType,
	ForwardSharedLocationContact,
	OldSharedLocationConfig,
	PartialOldSharedLocationConfig,
	RealTimeEvents
} from '../../types/shared-location.types';
import BaseCustomerExperienceStoreType from './base-customer-experience-store-type';
import RootStore from '../root-store';
import { RootEnv } from '../../helpers/create-store';
import { get as _get, omit as _omit, pick as _pick } from 'lodash';
import TrackingStore from '../tracking/tracking-store';
import { SHARING_TYPES_WITH_DYNAMIC_DISPLAY_TYPE } from '../../consts';

export enum SharedLocationState {
	UnInitialized,
	Initialized,
	Forward,
	Forwarded,
	Expired
}

class SharedLocationStore {
	sharedLocationState: SharedLocationState = SharedLocationState.UnInitialized;

	sharingType: SharingType = null;

	sharedLocationConfig: BaseSharedLocationConfig = null;

	constructor() {
		makeObservable(this, {
			sharedLocationState: observable,
			sharingType: observable,
			sharedLocationConfig: observable,
			setSharedLocationState: action,
			setSharingType: action,
			setSharedLocationConfig: action,
			isForwarded: computed,
			displayScreen: computed,
			allowCancel: computed,
			eventType: computed,
			allowCancelByEventType: computed
		});
	}

	get isForwarded() {
		return _get(this.sharedLocationConfig, 'forwarded', false);
	}

	get displayScreen(): SharedLocationDisplayType {
		if (!SHARING_TYPES_WITH_DYNAMIC_DISPLAY_TYPE.includes(this.sharingType)) {
			return;
		}

		return this.sharedLocationConfig.display_type;
	}

	setSharedLocationState(sharedLocationState: SharedLocationState) {
		this.sharedLocationState = sharedLocationState;
	}

	setSharingType(sharingType: SharingType) {
		this.sharingType = sharingType;
	}

	setSharedLocationConfig(sharedLocationConfig: BaseSharedLocationConfig) {
		this.sharedLocationConfig = sharedLocationConfig;
	}

	get allowCancel() {
		return (
			this.sharedLocationConfig.allow_cx_customer_cancellation && !this.sharedLocationConfig.isUserAuthenticated
		);
	}

	get eventType() {
		return _get(this.sharedLocationConfig, 'cx_cancel_event_type', 0);
	}

	get allowCancelByEventType() {
		return (
			(this.eventType === CancelEventType.DRIVER_ASSIGNED && !!!this.sharedLocationConfig?.driver_assigned) ||
			(this.eventType === CancelEventType.TASK_STARTED && !!!this.sharedLocationConfig?.task_started)
		);
	}

	private subscribeRealTimeEvents() {
		const { realTimeService } = getEnv<RootEnv>();
		realTimeService.listen(RealTimeEvents.OrderUpdated, this.setUpdatedConfigData);
	}

	private setUpdatedConfigData = task => {
		if (
			this.sharedLocationConfig.driver_assigned != task.driver?.id ||
			this.sharedLocationConfig.task_started != task.started_time
		) {
			this.setSharedLocationConfig({
				...this.sharedLocationConfig,
				driver_assigned: task.driver?.id,
				task_started: task.started_time,
				employee_name: task.driver?.name?.trim().split(' ')[0],
				job_description: task.driver?.job_description,
				employee_image: task.driver?.profile_image
			});
		}
	};

	async initialize(sharedLocationUuid: string): Promise<BaseSharedLocationConfig> {
		try {
			const { sharedLocationService, langService } = getEnv<RootEnv>();
			const oldSharedLocationConfig = await sharedLocationService.getSharedLocation(sharedLocationUuid);
			await langService.init(oldSharedLocationConfig.languageCode);

			this.handleSharedLocation(oldSharedLocationConfig);
			return this.sharedLocationConfig;
		} catch (error) {
			console.error(`Failed getting shared location: ${sharedLocationUuid}`, error);
			getRoot<RootStore>().setIsError(true);
		}
	}

	// Transform config to V2 shape and use it via
	// get customerExperienceStore.customerExperience proxy to support both V1 / V2 API
	serializeToV2 = (oldSharedLocationConfig: OldSharedLocationConfig): BaseSharedLocationConfig => {
		const uiDataProps = [
			'languageCode',
			'header_color',
			'merchant_logo',
			'bringg_logo',
			'eta_title_color',
			'vehicle_details_form_configuration',
			'timezone',
			'cx_scheduling_customer_message_title',
			'cx_scheduling_free_comment_input_label',
			'hide_powered_by_logo',
			'enforce_end_user_timezone'
		];

		const { delivery_window } = oldSharedLocationConfig;

		return {
			...(_omit(oldSharedLocationConfig, [
				...uiDataProps,
				'order_external_id',
				'delivery_window'
			]) as PartialOldSharedLocationConfig),
			ui_data: {
				..._pick(oldSharedLocationConfig, uiDataProps)
			},
			shared_location_uuid: oldSharedLocationConfig.uuid,
			customer: { name: oldSharedLocationConfig.customer_name },
			task: { external_id: oldSharedLocationConfig.order_external_id },
			waypoint: {
				no_earlier_than: _get(delivery_window, 'start_time'),
				no_later_than: _get(delivery_window, 'end_time')
			}
		};
	};

	getSharedLocationStoreBySharingType = (
		displayType: SharedLocationDisplayType
	): BaseCustomerExperienceStoreType<BaseSharedLocationConfig | BaseCustomerExperienceData> => {
		const { scheduleStore, driverTrackingStore } = getRoot<RootStore>();

		switch (this.sharingType) {
			case SharingType.Reschedule:
			case SharingType.Schedule:
				return scheduleStore;

			case SharingType.CustomWorkflow:
			case SharingType.NotificationByTemplateId:
				switch (displayType) {
					case SharedLocationDisplayType.Schedule:
						return scheduleStore;

					default:
						return driverTrackingStore;
				}

			default:
				return driverTrackingStore;
		}
	};

	handleSharedLocation = (oldSharedLocationConfig: OldSharedLocationConfig) => {
		const { expired, shared_type } = oldSharedLocationConfig;

		let serializedConfig;

		if (expired) {
			this.expiredMode();
			serializedConfig = this.serializeToV2(oldSharedLocationConfig);
			this.setSharedLocationConfig(serializedConfig);
			return;
		}

		const { realTimeService } = getEnv<RootEnv>();
		realTimeService.init();
		this.subscribeRealTimeEvents();

		this.setSharingType(shared_type);

		const storeToInitialize = this.getSharedLocationStoreBySharingType(oldSharedLocationConfig.display_type);

		const serializedStoreConfig = storeToInitialize.serializeToV2(oldSharedLocationConfig);
		serializedConfig = this.serializeToV2(serializedStoreConfig);

		this.setSharedLocationConfig(serializedConfig);

		storeToInitialize.initialize();

		if (this.isForwarded) {
			this.forwardedMode();
			return;
		}

		this.initializedMode();
	};

	getActiveTrackingStore = (): TrackingStore => {
		const { shared_type: sharedType, display_type: displayType } = this.sharedLocationConfig;
		const { driverTrackingStore, customerTrackingStore } = getRoot<RootStore>();

		// TODO: add here different tracking stores for share location update
		if (
			sharedType === SharingType.CustomerPickup ||
			(SHARING_TYPES_WITH_DYNAMIC_DISPLAY_TYPE.includes(sharedType) &&
				displayType === SharedLocationDisplayType.Pickup)
		) {
			return customerTrackingStore;
		}

		return driverTrackingStore; // default
	};

	rate = async (rating: number, reason_ids: number[], comment?: string) => {
		const sharedLocation = this.sharedLocationConfig;
		const { sharedLocationService } = getEnv<RootEnv>();
		try {
			await sharedLocationService.postRating({
				rating,
				reason_ids,
				comment,
				sharedLocationUuid: sharedLocation.uuid,
				token: sharedLocation.rating_token
			});
		} catch (e) {
			console.error(e);
			getRoot<RootStore>().setIsError(true);
		}
	};

	forward = async (forwardToContact: ForwardSharedLocationContact) => {
		const { sharedLocationService } = getEnv<RootEnv>();
		const { uuid } = this.sharedLocationConfig;

		const forwardParams = {
			uuid,
			...forwardToContact
		};

		await sharedLocationService.forward(forwardParams);
		this.initializedMode();
	};

	acceptForward = async () => {
		const { sharedLocationService } = getEnv<RootEnv>();
		const { uuid } = this.sharedLocationConfig;

		await sharedLocationService.acceptForward(uuid);
		this.initializedMode();
	};

	declineForward = async () => {
		const { sharedLocationService } = getEnv<RootEnv>();
		const { uuid } = this.sharedLocationConfig;

		await sharedLocationService.declineForward(uuid);
		this.expiredMode();
	};

	redirectOrder = async (teamId: number) => {
		const { sharedLocationService } = getEnv<RootEnv>();
		const { uuid } = this.sharedLocationConfig;

		await sharedLocationService.redirectOrder(teamId, uuid);
	};

	forwardedMode = () => this.setSharedLocationState(SharedLocationState.Forwarded);
	forwardMode = () => this.setSharedLocationState(SharedLocationState.Forward);
	initializedMode = () => this.setSharedLocationState(SharedLocationState.Initialized);
	expiredMode = () => this.setSharedLocationState(SharedLocationState.Expired);
}

export { SharedLocationStore };
